import { useMemo, useState } from "react";
import { toast } from "sonner";
import { RegulifyService } from "../../api/RegulifyService";
import LoadingCard from "../LoadingCard";

const LiteratureFileTable = ({ files, productName, loading }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadedFilter, setDownloadedFilter] = useState("");
  const [missingFilter, setMissingFilter] = useState("");

  const handleDownload = async (file) => {
    const file_name = file.file_name ?? file.filename ?? "";
    if (!file_name) {
      toast.error("File not downloaded yet.");
      return;
    }
    const downloadKey = file.file_id ?? file.id ?? file.file_name ?? file.pmid;
    setDownloadingId(downloadKey);
    try {
      const blob = await RegulifyService.dla_download_literature_file(
        productName,
        file_name,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename || file.file_name || "document.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started.");
    } catch (e) {
      toast.error("Download failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadedFiles = Array.isArray(files)
    ? files.filter((file) => Boolean(file.file_name ?? file.name))
    : [];
  const missingFiles = Array.isArray(files)
    ? files.filter((file) => !Boolean(file.file_name ?? file.name))
    : [];

  const filteredDownloaded = useMemo(() => {
    if (!downloadedFilter.trim()) return downloadedFiles;
    const q = downloadedFilter.trim().toLowerCase();
    return downloadedFiles.filter((file) => {
      const fileName = (file.file_name ?? file.name ?? "").toLowerCase();
      const pmid = String(file.pmid ?? "").toLowerCase();
      return fileName.includes(q) || pmid.includes(q);
    });
  }, [downloadedFiles, downloadedFilter]);

  const filteredMissing = useMemo(() => {
    if (!missingFilter.trim()) return missingFiles;
    const q = missingFilter.trim().toLowerCase();
    return missingFiles.filter((file) => {
      const pmid = String(file.pmid ?? "").toLowerCase();
      return pmid.includes(q);
    });
  }, [missingFiles, missingFilter]);

  if (loading) {
    return (
      <div className="py-8">
        <LoadingCard
          message="Loading literature files…"
          subMessage="Collecting downloaded papers."
        />
      </div>
    );
  }

  if (!files?.length) {
    return (
      <div className="rounded-xl border border-base-300 bg-base-100 p-8 text-center text-base-content/70">
        No literature files for this device yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100 shadow-sm">
        <div className="border-b border-base-200 px-4 py-3">
          <input
            type="text"
            className="input input-bordered w-full"
            placeholder="Search downloaded files by name or PMID"
            value={downloadedFilter}
            onChange={(e) => setDownloadedFilter(e.target.value)}
          />
        </div>
        <table className="table table-zebra">
          <thead>
            <tr className="bg-base-200">
              <th className="font-semibold text-base-content">Filename</th>
              <th className="font-semibold text-base-content">PMID</th>
              <th className="font-semibold text-base-content text-right">Download</th>
            </tr>
          </thead>
          <tbody>
            {filteredDownloaded.map((file, idx) => {
              const fileName = file.file_name ?? file.name ?? "";
              const downloadKey =
                file.file_id ?? file.id ?? file.file_name ?? file.pmid ?? idx;
              return (
                <tr key={downloadKey} className="hover:bg-base-200/50">
                  <td className="font-medium">{fileName}</td>
                  <td>{file.pmid ?? "—"}</td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-square"
                      onClick={() => handleDownload(file)}
                      disabled={downloadingId === downloadKey}
                      title="Download"
                    >
                      {downloadingId === downloadKey ? (
                        <span className="loading loading-spinner loading-sm" />
                      ) : (
                        <span className="text-lg">⬇</span>
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredDownloaded.length === 0 && (
              <tr>
                <td colSpan={3} className="py-6 text-center text-base-content/70">
                  No downloaded files yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {missingFiles.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100 shadow-sm">
          <div className="border-b border-base-200 px-4 py-3">
            <div className="text-sm font-semibold text-base-content">
              Non Downloaded PMIDs
            </div>
            <input
              type="text"
              className="input input-bordered mt-3 w-full"
              placeholder="Search non-downloaded PMIDs"
              value={missingFilter}
              onChange={(e) => setMissingFilter(e.target.value)}
            />
          </div>
          <table className="table table-zebra">
            <thead>
              <tr className="bg-base-200">
                <th className="font-semibold text-base-content">PMID</th>
                <th className="font-semibold text-base-content">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredMissing.map((file, idx) => (
                <tr key={file.pmid ?? idx} className="hover:bg-base-200/50">
                  <td>{file.pmid ?? "—"}</td>
                  <td className="text-base-content/60">Not downloaded</td>
                </tr>
              ))}
              {filteredMissing.length === 0 && (
                <tr>
                  <td colSpan={2} className="py-6 text-center text-base-content/70">
                    No matching PMIDs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LiteratureFileTable;
